import { useEffect, useState } from "react";
import { vendorsApi, vatApi, businessesApi, accountsApi } from "@/lib/api";
import { useAuth, isMaster, userBusinessIds } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, Trash2, RefreshCcw, Loader2, X, Save } from "lucide-react";

const PAYMENT_METHODS = ["", "Cash", "Card", "EFT", "Credit", "Cheque", "Other"];
const DOC_TYPES = ["", "bill", "expense"];

const EMPTY = {
  name: "", external_id: "",
  default_expense_account: "", default_tax_code_id: "",
  default_branch_id: "", default_payment_method: "",
  default_document_type: "", auto_create_missing: false,
  aliases: [],
};

export default function Vendors() {
  const { user } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [bizId, setBizId] = useState("");
  const [vendors, setVendors] = useState([]);
  const [vats, setVats] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [syncingAccounts, setSyncingAccounts] = useState(false);
  const [branches, setBranches] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState("");

  const biz = businesses.find((b) => b.id === bizId);

  useEffect(() => {
    businessesApi.list().then((b) => {
      setBusinesses(b);
      const initial = isMaster(user) ? (b[0]?.id || "") : userBusinessIds(user)[0] || "";
      setBizId(initial);
    });
  }, [user]);

  const load = () => {
    if (!bizId) return;
    vendorsApi.list(bizId).then(setVendors);
    vatApi.list(bizId).then(setVats);
    accountsApi.list(bizId).then(setAccounts).catch(() => setAccounts([]));
    businessesApi.branches(bizId).then(setBranches).catch(() => setBranches([]));
  };
  useEffect(load, [bizId]);

  const syncAccounts = async () => {
    setSyncingAccounts(true);
    try {
      const res = await accountsApi.syncFromAccounting(bizId);
      toast.success(`Synced ${res.total} active accounts (+${res.created} new, ~${res.updated} updated${res.removed ? `, ${res.removed} inactive removed` : ""})`);
      accountsApi.list(bizId).then(setAccounts);
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setSyncingAccounts(false); }
  };

  const openNew = () => { setEditing("new"); setForm(EMPTY); };
  const openEdit = (v) => { setEditing(v.id); setForm({ ...EMPTY, ...v }); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    try {
      await vendorsApi.upsert(form, bizId);
      toast.success("Vendor rule saved");
      cancel();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this vendor rule?")) return;
    await vendorsApi.del(id);
    load();
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await vendorsApi.syncFromAccounting(bizId);
      toast.success(`Synced ${res.total} active vendors (+${res.created} new, ~${res.updated} updated${res.removed ? `, ${res.removed} inactive removed` : ""})`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const filtered = vendors.filter((v) => !search || v.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-bold text-espresso">Vendor rules</h1>
          <p className="text-espresso-ink/60 mt-2">Sync your suppliers from {biz?.accounting === "xero" ? "Xero" : "Zoho Books"} and set auto-mapping rules (expense account, VAT, branch, payment method, document type).</p>
        </div>
        <div className="flex gap-2">
          {(isMaster(user) || userBusinessIds(user).length > 1) && (
            <select data-testid="vendors-biz" value={bizId} onChange={(e) => setBizId(e.target.value)} className="px-3 py-2 bg-white border border-canvas-border text-sm brand-focus">
              {(isMaster(user) ? businesses : businesses.filter((b) => userBusinessIds(user).includes(b.id))).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          <button onClick={openNew} data-testid="add-vendor" className="inline-flex items-center gap-2 px-4 py-2 bg-espresso text-canvas text-sm font-semibold hover:bg-espresso-light">
            <Plus className="w-4 h-4" /> Add rule
          </button>
        </div>
      </div>

      {!biz?.connected && (
        <div className="p-3 bg-status-pending/10 border border-status-pending/30 text-status-pending text-sm">
          Connect {biz?.accounting === "xero" ? "Xero" : "Zoho Books"} on the Integrations page to enable vendor sync.
        </div>
      )}

      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search vendors…" className="w-full max-w-sm px-3 py-2 bg-white border border-canvas-border text-sm brand-focus" data-testid="vendor-search" />

      {editing && (
        <form onSubmit={save} className="bg-white border-2 border-gold p-6 shadow-soft space-y-5" data-testid="vendor-form">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-bold text-espresso">{editing === "new" ? "New vendor rule" : "Edit vendor rule"}</h2>
            <button type="button" onClick={cancel} className="text-espresso-ink/50 hover:text-espresso"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <F label="Vendor name" testid="v-name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <F label="Accounting Contact ID" testid="v-extid" value={form.external_id} onChange={(v) => setForm({ ...form, external_id: v })} placeholder="Auto-populated on sync" />
            <div>
              <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">
                Default expense account
              </label>
              {accounts.length > 0 ? (
                <select
                  data-testid="v-account"
                  value={form.default_expense_account || ""}
                  onChange={(e) => setForm({ ...form, default_expense_account: e.target.value })}
                  className="w-full mt-1.5 px-3 py-2.5 border border-canvas-border brand-focus"
                >
                  <option value="">— none —</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={biz?.accounting === "xero" ? (a.code || a.external_id) : a.external_id}>
                      {a.code ? `${a.code} · ` : ""}{a.name}{a.type ? ` (${a.type})` : ""}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  data-testid="v-account"
                  value={form.default_expense_account || ""}
                  onChange={(e) => setForm({ ...form, default_expense_account: e.target.value })}
                  placeholder="Sync chart of accounts to pick from a list, or type an account code"
                  className="w-full mt-1.5 px-3 py-2.5 border border-canvas-border brand-focus"
                />
              )}
            </div>
            <FS label="Default VAT code" testid="v-vat" value={form.default_tax_code_id} onChange={(v) => setForm({ ...form, default_tax_code_id: v })} options={[{ value: "", label: "—" }, ...vats.map((x) => ({ value: x.id, label: `${x.name} (${x.rate}%)` }))]} />
            <FS label="Default branch" testid="v-branch" value={form.default_branch_id} onChange={(v) => setForm({ ...form, default_branch_id: v })} options={[{ value: "", label: "—" }, ...branches.map((x) => ({ value: x.id, label: x.name }))]} />
            <FS label="Default payment method" testid="v-payment" value={form.default_payment_method} onChange={(v) => setForm({ ...form, default_payment_method: v })} options={PAYMENT_METHODS.map((p) => ({ value: p, label: p || "—" }))} />
            {biz?.accounting === "zoho" && (
              <FS label="Default document type" testid="v-doctype" value={form.default_document_type} onChange={(v) => setForm({ ...form, default_document_type: v })} options={DOC_TYPES.map((p) => ({ value: p, label: p ? p.charAt(0).toUpperCase() + p.slice(1) : "—" }))} />
            )}
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Aliases (comma-separated) — alternate names OCR may see</label>
            <input value={(form.aliases || []).join(", ")} onChange={(e) => setForm({ ...form, aliases: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="w-full mt-1.5 px-3 py-2.5 border border-canvas-border brand-focus" data-testid="v-aliases" />
          </div>
          <label className="flex items-center gap-2 text-sm text-espresso-ink">
            <input type="checkbox" checked={form.auto_create_missing} onChange={(e) => setForm({ ...form, auto_create_missing: e.target.checked })} data-testid="v-auto" />
            Auto-create vendor in accounting if missing
          </label>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={cancel} className="px-4 py-2 border border-canvas-border text-sm font-semibold text-espresso">Cancel</button>
            <button type="submit" data-testid="v-save" className="inline-flex items-center gap-2 px-4 py-2 bg-espresso text-canvas text-sm font-semibold hover:bg-espresso-light"><Save className="w-4 h-4" /> Save rule</button>
          </div>
        </form>
      )}

      <div className="bg-white border border-canvas-border overflow-hidden shadow-soft">
        {filtered.length === 0 ? (
          <div className="p-14 text-center text-espresso-ink/50">{search ? "No matches." : "No vendors yet — sync from accounting or add manually."}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-canvas text-xs uppercase tracking-wider text-espresso-ink/60">
              <tr>
                <th className="text-left px-4 py-3">Vendor</th>
                <th className="text-left px-4 py-3">Account</th>
                <th className="text-left px-4 py-3">VAT</th>
                <th className="text-left px-4 py-3">Branch</th>
                <th className="text-left px-4 py-3">Payment</th>
                {biz?.accounting === "zoho" && <th className="text-left px-4 py-3">Type</th>}
                <th className="text-left px-4 py-3">Seen</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((v) => (
                <tr key={v.id} className="border-t border-canvas-border">
                  <td className="px-4 py-3 font-medium text-espresso">
                    {v.name}
                    {v.external_id && <span className="ml-2 text-[10px] uppercase tracking-widest bg-canvas-border text-espresso-ink/70 px-1.5 py-0.5 rounded-sm">SYNCED</span>}
                  </td>
                  <td className="px-4 py-3 text-espresso-ink">{v.default_expense_account || "—"}</td>
                  <td className="px-4 py-3 text-espresso-ink">{vats.find((x) => x.id === v.default_tax_code_id)?.name || "—"}</td>
                  <td className="px-4 py-3 text-espresso-ink">{branches.find((x) => x.id === v.default_branch_id)?.name || "—"}</td>
                  <td className="px-4 py-3 text-espresso-ink">{v.default_payment_method || "—"}</td>
                  {biz?.accounting === "zoho" && <td className="px-4 py-3 text-espresso-ink capitalize">{v.default_document_type || "—"}</td>}
                  <td className="px-4 py-3 text-espresso-ink/70">{v.seen_count || 0}</td>
                  <td className="px-3 py-3 text-right space-x-2">
                    <button onClick={() => openEdit(v)} className="text-xs text-espresso underline" data-testid={`edit-vendor-${v.id}`}>Edit</button>
                    <button onClick={() => del(v.id)} className="text-status-duplicate hover:opacity-70"><Trash2 className="w-3.5 h-3.5 inline" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function F({ label, value, onChange, required, placeholder, testid, type = "text" }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">{label}</label>
      <input data-testid={testid} type={type} required={required} placeholder={placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full mt-1.5 px-3 py-2.5 border border-canvas-border brand-focus" />
    </div>
  );
}

function FS({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">{label}</label>
      <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full mt-1.5 px-3 py-2.5 border border-canvas-border brand-focus">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
